<template>
  <div
    v-if="icon && showing === 'svg'"
    id="code-snippet-svg"
    class="code-snippet-tab"
    role="tabpanel"
    aria-labelledby="tab-svg"
  >
    <div
      v-if="hasFullSvgSupport"
      class="padding-top-sm padding-x-xl"
    >
      <ul
        class="code-snippet-secondary-tabs size-sm position-relative z-1"
        role="tablist"
        style="
          --list-style-type: none;
          --padding-left: 0;
          --margin-bottom: 0;
          --button-padding: var(--spacing-2xs) var(--spacing-sm);
          --button-border-radius: 0;
          --button-margin-bottom: 0;
        "
      >
        <li
          v-for="option in options"
          :key="option"
          style="--display: inline-block"
          role="presentation"
        >
          <button
            :id="`tab-${sluggifyOption(option)}`"
            class="link flat block code-snippet-secondary-tab"
            style="--button-active-background: transparent"
            type="button"
            role="tab"
            :aria-controls="`${sluggifyOption(option)}`"
            :aria-selected="sluggifyOption(option) === formatChoice"
            @click="showOption(option)"
          >
            {{ option }}
          </button>
        </li>
      </ul>
    </div>

    <owa-code-block
      add-highlighting
      allow-shadow-copy
      copy-text="Copy Code Snippet"
      custom-copy-icon="faClone"
      copy-tracking-event="icon_svg_code_copy_button_click"
      :copy-tracking-params="{ icon_name: icon.name, icon_style: styling.style, icon_family: styling.family, svg_format: formatChoice }"
      lang="svg"
      :code="snippet"
    />

    <icon-code-snippet-links
      :has-custom-styling="hasCustomStyling"
      :links="svgLearnAboutLinks"
      :selected-style="styling.style"
      :major-version="majorVersion"
    />
  </div>
</template>

<script>
  import IconCodeSnippetLinks from './IconCodeSnippetLinks'
  import { svgStylingDifference, svgCodeSnippet, fetchSvg } from './utils'
  import sluggify from 'app/utils/sluggify'
  import { hasFullSvgSupport } from 'app/releases'

  export default {
    name: 'IconCodeSnippetSvg',

    components: {
      IconCodeSnippetLinks
    },

    props: {
      activeVersion: {
        required: false
      },
      defaultStyles: {
        type: Object
      },
      icon: {
        type: Object
      },
      hasCustomStyling: {
        type: Boolean
      },
      kitVersion: {
        type: Number,
        required: false
      },
      showing: {
        required: false
      },
      styling: {
        type: Object
      },
      svgSource: {
        type: String
      },
      majorVersion: {
        type: Number
      },
      isCustomIcon: {
        type: Boolean,
        default: false
      },
      viggo: {
        validator(value) {
          return !!value
        }
      }
    },

    data() {
      return {
        formatChoice: 'svg',
        iconAbstract: null,
        snippet: ''
      }
    },

    computed: {
      hasFullSvgSupport() {
        return hasFullSvgSupport(this.isCustomIcon ? this.kitVersion : this.majorVersion)
      },

      options() {
        if (this.hasFullSvgSupport) return ['Full SVG', 'SVG']
        return ['SVG']
      },

      svgLearnAboutLinks() {
        const label = 'Using Bare SVGs'

        const path = this.majorVersion === 5 ? '/web/advanced/svg-sprites' : '/web/add-icons/svg-bare'

        return [{ label, path }]
      }
    },

    watch: {
      formatChoice(newValue) {
        this.$emit('svg-format-change', newValue)
      },

      styling: {
        handler() {
          this.fetchSVG()
        },
        deep: true
      },

      icon: {
        handler() {
          this.loadSavedPreference()
        },
        immediate: true
      },

      options: {
        handler() {
          this.loadSavedPreference()
        },
        deep: true
      }
    },

    mounted() {
      this.loadSavedPreference()
      // emit the initial format based on version support or saved preference
      this.$emit('svg-format-change', this.formatChoice)

      // Fetch SVG after preferences are loaded
      this.fetchSVG()
    },

    methods: {
      loadSavedPreference() {
        // Load saved SVG format option from localStorage
        const savedOption = localStorage.getItem('fa-code-snippet-option-svg')
        const isSavedOptionValid = this.options.some((option) => this.sluggifyOption(option) === savedOption)

        // Only use saved preference if it's currently available
        if (savedOption && isSavedOptionValid) {
          this.formatChoice = savedOption
        } else {
          // Default based on version support if no saved preference or not available
          this.hasFullSvgSupport ? (this.formatChoice = 'full_svg') : (this.formatChoice = 'svg')
        }
      },

      async copySvg() {
        this.$emit('copySvg')
      },

      async showOption(option) {
        const newFormatChoice = this.sluggifyOption(option)

        // Save the user's preference to localStorage
        localStorage.setItem('fa-code-snippet-option-svg', newFormatChoice)

        this.formatChoice = newFormatChoice
        await this.fetchSVG()
      },

      sluggifyOption(option) {
        return sluggify(option)
      },

      async fetchSVG() {
        if (this.viggo) {
          const isFullSvg = this.formatChoice === 'full_svg'

          fetchSvg({
            viggo: this.viggo,
            isCustomIcon: this.isCustomIcon,
            icon: this.icon,
            activeVersion: this.$releases.active.version,
            styling: this.styling,
            isFullSvg
          })
            .then((iconAbstract) => {
              this.iconAbstract = iconAbstract

              const colors = svgStylingDifference(this.styling, this.defaultStyles)
              const iconLicenseType = this.icon?.is_free ? 'Free' : 'Pro'
              this.snippet = svgCodeSnippet({
                viggo: this.viggo,
                iconAbstract: this.iconAbstract,
                svgStylingDifference: colors,
                iconLicenseType,
                version: this.activeVersion
              })
            })
            .catch((error) => {
              console.error(error)
              throw new Error('fetchSvg() in watcher', { cause: error })
            })
        }
      }
    }
  }
</script>
